import { useCallback, useEffect, useMemo, useState } from 'react';

export const useLocalMessagePermission = (key, roomId, subChannelId, isSubChannelPrivate, initialValue, addInLocal) => {
    const validPermissionValue = useMemo(
        () => (subChannelId ? ['owner', 'user'] : ['family', 'user', 'doctor', 'supervisor']),
        [subChannelId]
    );
    const fallbackValue = useMemo(
        () => (validPermissionValue.includes(initialValue) ? [initialValue] : ['user']),
        [initialValue, validPermissionValue]
    );
    const isStoredValueValid = useCallback(
        (value) =>
            Array.isArray(value) && value.length > 0 && value.every((role) => validPermissionValue.includes(role)),
        [validPermissionValue]
    );

    const getPermissions = useCallback(() => {
        const storageId = subChannelId || roomId;
        try {
            if (typeof window === 'undefined' || !window.localStorage) return fallbackValue;
            const item = window.localStorage.getItem(key);
            const parsedItem = item ? JSON.parse(item) : {};
            if (isStoredValueValid(parsedItem?.[storageId])) return parsedItem[storageId];
            parsedItem[storageId] = fallbackValue;
            if (subChannelId ? isSubChannelPrivate : addInLocal) {
                window.localStorage.setItem(key, JSON.stringify(parsedItem));
            }
            return fallbackValue;
        } catch (error) {
            console.warn('Could not read message permissions from localStorage:', error);
            return fallbackValue;
        }
    }, [addInLocal, fallbackValue, isStoredValueValid, isSubChannelPrivate, key, roomId, subChannelId]);

    const [storedValue, setStoredValue] = useState(() => getPermissions());

    useEffect(() => {
        setStoredValue(getPermissions());
    }, [getPermissions]);

    const setValue = (value) => {
        const safeValue = isStoredValueValid(value) ? value : fallbackValue;
        setStoredValue(safeValue);
        try {
            if (typeof window === 'undefined' || !window.localStorage) return;
            const newItem = window.localStorage.getItem(key);
            const parsedItem = newItem ? JSON.parse(newItem) : {};
            parsedItem[subChannelId || roomId] = safeValue;
            window.localStorage.setItem(key, JSON.stringify(parsedItem));
        } catch (error) {
            console.warn('Could not save message permissions to localStorage:', error);
        }
    };

    return [storedValue, setValue];
};

export default useLocalMessagePermission;
